import { Component, OnInit, inject, effect, computed, ChangeDetectionStrategy, untracked, signal, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { PaymentService } from '../../services/payment.service';
import { PeriodService } from '../../../billing-management/services/period.service';
import { EntityBillingService } from '../../../billing-management/services/entity-billing.service';
import { CatalogService } from '@services/data/catalog.service';
import { StorageService } from '@services/core/storage.service';
import Swal from 'sweetalert2';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Global } from '@services/core/Global';
import { PaymentFormComponent } from '../../components/payment-form/payment-form.component';
import { BpmService } from 'app/feature/management-request/services/bpm.service';
import { AdvancedFilterComponent, FilterField } from 'app/core/components/advanced-filter/advanced-filter.component';
import { ListCardComponent } from 'app/core/components/list-card/list-card.component';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { AppSettings } from '@services/settings/app-settings.service';
import { AsinetExportService, AsinetRow } from '@services/export/asinet-export.service';
import { AuthService } from '@services/core/auth.service';
import { PermissionService } from '@services/core/permission.service';

@Component({
  selector: 'app-payment-management',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, AdvancedFilterComponent, ListCardComponent, PageHeaderComponent, MobileFooterComponent],
  templateUrl: './payment-management.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./payment-management.component.scss']
})
export class PaymentManagementComponent implements OnInit {
  public appSettings = inject(AppSettings);
  readonly perm = inject(PermissionService);
  advancedFilterFields: FilterField[] = [
    { key: 'description', label: 'Glosa / Descripción' },
    { key: 'amount', label: 'Monto (Cargo o Abono)' },
    { key: 'entityCode', label: 'Código Entidad' }
  ];
  activeAdvancedFilters: Array<{ field: string; value: string }> = [];

  periods: any[] = [];
  entities: any[] = [];
  movements: any[] = [];
  allMovements: any[] = []; // Maestro para filtrado local
  
  selectedPeriodStartId: number | null = null;
  selectedPeriodEndId: number | null = null;
  filteredEndPeriods: any[] = [];
  selectedEntityCode: any[] = []; // Multiselección
  
  isLoading = true;
  hasLoadedData = false;
  totalIngresos = 0;
  totalEgresos = 0;
  totalMargen = 0;

  // Signals para modal ASINET de abonos (todos: pendientes + contabilizados)
  asinetPayments = signal<any[]>([]);
  selectedPaymentUuids = signal<Set<string>>(new Set());
  isPaymentExportLoading = signal<boolean>(false);
  /** Periodo cerrado seleccionado en el modal ASINET (independiente del rango de la página). */
  asinetPeriodId = signal<number | null>(null);
  closedPeriods = computed(() => this.periodService.closedBillingPeriods());
  asinetPeriod = computed(() => this.closedPeriods().find((p) => p.id === this.asinetPeriodId())
    ?? this.periods.find((p) => p.id === this.asinetPeriodId())
    ?? null);
  selectedAsinetPayments = computed(() => {
    const selected = this.selectedPaymentUuids();
    return this.asinetPayments().filter((p) => p.uuid && selected.has(p.uuid));
  });
  selectedPendingPayments = computed(() =>
    this.selectedAsinetPayments().filter((p) => !p.isAccounted)
  );
  selectedPaymentsTotal = computed(() =>
    this.selectedAsinetPayments().reduce((sum, p) => sum + Number(p.credit ?? 0), 0)
  );
  selectedPendingTotal = computed(() =>
    this.selectedPendingPayments().reduce((sum, p) => sum + Number(p.credit ?? 0), 0)
  );
  allPendingSelected = computed(() => {
    const pending = this.asinetPayments().filter((p) => !p.isAccounted && p.uuid);
    if (!pending.length) return false;
    const selected = this.selectedPaymentUuids();
    return pending.every((p) => selected.has(p.uuid));
  });
  hasPendingAsinetPayments = computed(() =>
    this.asinetPayments().some((p) => !p.isAccounted && p.uuid)
  );

  // Modal cobro consolidado a entidades (ASINET)
  entityBillingItems = signal<any[]>([]);
  entityBillingTotal = signal<number>(0);
  isEntityBillingLoading = signal<boolean>(false);
  entityAccountedInfo = computed(() => {
    const p = this.asinetPeriod();
    if (!p?.isEntityAccounted) return null;
    const user = p.entityAccountedUser?.displayName ?? p.entityAccountedUser?.mail ?? 'Usuario';
    const date = p.entityAccountedAt ? new Date(p.entityAccountedAt).toLocaleDateString() : '';
    return { user, date };
  });

  private paymentService = inject(PaymentService);
  private periodService = inject(PeriodService);
  private entityBillingService = inject(EntityBillingService);
  private catalogService = inject(CatalogService);
  private authService = inject(AuthService);
  private storageService = inject(StorageService);
  private modalService = inject(NgbModal);
  private sanitizer = inject(DomSanitizer);
  private bpmService = inject(BpmService);
  private asinetExport = inject(AsinetExportService);

  pdfSrc: SafeResourceUrl | null = null;
  previewType: 'pdf' | 'image' = 'pdf';
  imgSrc: string | null = null;
  previewFileName: string | null = null;
  previewRawUrl: string | null = null;

  // Filtro de tipos de movimiento para la tabla
  movementTypeFilter = 'ALL';
  movementTypes = [
    { code: 'ALL', name: 'Todos los movimientos' },
    { code: 'PAGO', name: 'Solo Pagos (Abonos)' },
    { code: 'COBRO', name: 'Solo Cobros (Deuda)' }
  ];
  /** Por defecto ocultar ya contabilizados en cartola (contabilidad revisa pendientes). */
  selectedAccounted = 'false';
  accountedFilters = [
    { slug: '', name: 'Todos' },
    { slug: 'false', name: 'Pendiente de contabilizar' },
    { slug: 'true', name: 'Contabilizado' }
  ];

  // Valida dinámicamente si el rol del usuario puede iniciar ajustes en BPM
  canRegisterAdjustment = computed(() => {
    if (!this.perm.canCurrent('canCreate')) return false;
    const role = this.storageService.getJsonValue('role');
    const slug = String(role?.slug ?? role?.name ?? '').toUpperCase();
    return this.bpmService.canInitiateProcess('ADJUST_ENTITY', slug);
  });

  constructor() {
    effect(() => {
      this.loadEntities();
    });
    effect(() => {
      const list = this.periodService.billingPeriods();
      const rev = this.periodService.revision();
      if (!rev && !list.length) return;
      untracked(() => this.applyPeriods(list));
    });
  }

  ngOnInit(): void {
    if (!this.periodService.periods().length) this.periodService.reload();
    this.loadEntities();
  }

  loadEntities() {
    const assigned = (this.authService.sharingEntities() as any[]) || [];
    const catalog = this.catalogService.entities() || [];
    const byCode = new Map(catalog.map((e: any) => [Number(e.code), e]));
    this.entities = assigned
      .map((e: any) => {
        const code = Number(e.code ?? e.entityCode);
        if (!Number.isFinite(code)) return null;
        const cat = byCode.get(code);
        const name = cat?.name ?? e.name ?? '';
        return { ...(cat || {}), ...e, code, name: `${code} - ${name}` };
      })
      .filter(Boolean) as any[];
  }

  // Obtiene nombre descriptivo de la entidad
  getEntityName(code: any): string {
    const ent = this.entities.find((e) => Number(e.code) === Number(code));
    return ent?.name ?? `Entidad ${code ?? ''}`;
  }

  hasReceipt(move: any): boolean {
    return move?.type === 'PAGO' && !!move?.document?.uuid;
  }

  /** Vista previa del comprobante de abono (imagen o PDF) para contabilidad / ASINET. */
  viewReceipt(move: any, template: TemplateRef<any>): void {
    const doc = move?.document;
    if (!doc?.uuid) {
      Swal.fire('Sin comprobante', 'Este abono no tiene documento de respaldo adjunto.', 'info');
      return;
    }
    const url = `${Global.url}V1/documents/view/${doc.uuid}`;
    const isImg = String(doc.mimeType || '').startsWith('image/');
    this.previewType = isImg ? 'image' : 'pdf';
    this.previewRawUrl = url;
    this.imgSrc = isImg ? url : null;
    this.pdfSrc = isImg ? null : this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.previewFileName = doc.fileName || 'comprobante';
    this.modalService.open(template, { size: 'xl', backdrop: 'static' });
  }

  /** true/false = estado conocido; null = no aplica (sin dato). */
  isMovementAccounted(move: any): boolean | null {
    if (!move) return null;
    if (move.type === 'PAGO' || move.uuid) return Boolean(move.isAccounted);
    // Cobros en cartola de entidades → contabilización del cobro a entidades del periodo
    const period = this.periods.find((p) =>
      Number(p.id) === Number(move.periodId)
      || (Number(p.year) === Number(move.year) && Number(p.period) === Number(move.period))
    );
    if (!period) return null;
    return Boolean(period.isEntityAccounted);
  }

  applyPeriods(list: any[]) {
    this.periods = list;
    if (this.periods.length > 0) {
      const range = this.periodService.defaultClosedYearRangeIds();
      if (range) {
        this.selectedPeriodStartId = range.startId;
        this.selectedPeriodEndId = range.endId;
      } else {
        // Sin cerrados aún: solo consulta del periodo activo (previa).
        const activeId = this.periodService.activePeriod()?.id ?? this.periods[0].id;
        this.selectedPeriodStartId = activeId;
        this.selectedPeriodEndId = activeId;
      }
      this.updateFilteredEndPeriods();
      this.loadAnalysis();
    }
  }

  updateFilteredEndPeriods() {
    if (!this.selectedPeriodStartId) return;
    const startPeriod = this.periods.find(p => p.id === this.selectedPeriodStartId);
    const startValue = startPeriod.year * 100 + startPeriod.period;
    
    this.filteredEndPeriods = this.periods.filter(p => (p.year * 100 + p.period) >= startValue);
    
    if (this.selectedPeriodEndId) {
      const endPeriod = this.periods.find(p => p.id === this.selectedPeriodEndId);
      if (endPeriod && (endPeriod.year * 100 + endPeriod.period) < startValue) {
        this.selectedPeriodEndId = this.selectedPeriodStartId;
      }
    }
  }

  initialBalance = 0;

  loadAnalysis() {
    this.isLoading = true;
    const filters: any = {
      periodStartId: this.selectedPeriodStartId,
      periodEndId: this.selectedPeriodEndId
    };
    if (this.selectedEntityCode && (Array.isArray(this.selectedEntityCode) ? this.selectedEntityCode.length > 0 : !!this.selectedEntityCode)) {
      filters.entityCode = this.selectedEntityCode;
    }

    this.paymentService.getAccountAnalysis(filters).subscribe({
      next: (res: any) => {
        this.initialBalance = Number(res?.initialBalance ?? 0);
        this.allMovements = Array.isArray(res) ? res : (res?.movements ?? []);
        this.filterMovements();
        this.isLoading = false;
        this.hasLoadedData = true;
      },
      error: (err) => {
        console.error(err);
        this.isLoading = false;
        this.hasLoadedData = true;
        Swal.fire('Error', 'No se pudo cargar el análisis de cuenta', 'error');
      }
    });
  }


  private filterMovementsByEntity() {
    if (!this.selectedEntityCode || (Array.isArray(this.selectedEntityCode) && this.selectedEntityCode.length === 0)) {
      this.movements = [...this.allMovements];
    } else {
      const selectedCodes = Array.isArray(this.selectedEntityCode) 
        ? this.selectedEntityCode.map(Number) 
        : [Number(this.selectedEntityCode)];
      this.movements = this.allMovements.filter(m => selectedCodes.includes(Number(m.entityCode)));
    }
  }


  // Aplica filtros de entidad y tipo de movimiento, calculando saldo acumulado
  filterMovements() {
    this.filterMovementsByEntity();
    this.movements.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    let currentBalance = this.initialBalance;
    this.movements = this.movements.map(m => {
      currentBalance += (Number(m.charge ?? 0) - Number(m.credit ?? 0));
      return { ...m, balance: currentBalance.toFixed(0) };
    });
    this.calculateTotals();
    this._applyTypeFilter();
    this._applyAccountedFilter();
    this._applyAdvancedFilters();
  }

  // Evalúa si un movimiento cumple con una regla del filtro avanzado
  private _matchPaymentRule(m: any, rule: { field: string; value: string }): boolean {
    const val = (rule?.value ?? '').toLowerCase();
    if (!val) return true;
    if (rule.field === 'description') return (m.description ?? '').toLowerCase().includes(val);
    if (rule.field === 'entityCode') return String(m.entityCode ?? '').toLowerCase().includes(val);
    if (rule.field === 'amount') {
      return String(m.charge ?? '').includes(val) || String(m.credit ?? '').includes(val);
    }
    return true;
  }

  // Aplica los filtros avanzados sobre la lista de movimientos
  private _applyAdvancedFilters(): void {
    if (!this.activeAdvancedFilters?.length) return;
    this.movements = this.movements.filter(m =>
      this.activeAdvancedFilters.every(rule => this._matchPaymentRule(m, rule))
    );
  }

  // Actualiza los filtros avanzados y recalcula la tabla de pagos
  onAdvancedFiltersChange(filters: Array<{ field: string; value: string }>): void {
    this.activeAdvancedFilters = filters ?? [];
    this.filterMovements();
  }

  // Filtra movimientos visibles según el tipo seleccionado
  private _applyTypeFilter() {
    if (this.movementTypeFilter && this.movementTypeFilter !== 'ALL') {
      this.movements = this.movements.filter(m => m.type === this.movementTypeFilter);
    }
  }

  // Filtra por estado de contabilización (igual que reembolso)
  private _applyAccountedFilter() {
    if (!this.selectedAccounted) return;
    const wantAccounted = this.selectedAccounted === 'true';
    this.movements = this.movements.filter((m) => this.isMovementAccounted(m) === wantAccounted);
  }

  calculateTotals() {
    this.totalEgresos = this.movements
      .filter(m => m.type === 'COBRO')
      .reduce((sum, m) => sum + Number(m.charge ?? 0), 0);
    
    this.totalIngresos = this.movements
      .filter(m => m.type === 'PAGO')
      .reduce((sum, m) => sum + Number(m.credit ?? 0), 0);
    
    this.totalMargen = this.initialBalance + this.totalEgresos - this.totalIngresos;
  }

  getTotalBalance() {
    return this.totalMargen;
  }

  openRegisterPayment(mode: 'PAYMENT' | 'ADJUSTMENT' = 'PAYMENT') {
    const modalRef = this.modalService.open(PaymentFormComponent, { size: 'lg', backdrop: 'static' });
    modalRef.componentInstance.mode = mode;
    if (this.selectedEntityCode && this.selectedEntityCode.length === 1) {
      modalRef.componentInstance.entityCode = this.selectedEntityCode[0];
    }
    
    modalRef.componentInstance.onSave.subscribe(() => {
      modalRef.close();
      this.loadAnalysis();
    });

    modalRef.componentInstance.onClose.subscribe(() => {
      modalRef.close();
    });
  }

  exportExcel(): void {
    if (!this.movements || this.movements.length === 0) return;
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Análisis de Cuenta');

    worksheet.columns = [
      { header: 'Fecha', key: 'date', width: 15 },
      { header: 'Descripción', key: 'description', width: 40 },
      { header: 'Cargo (Cobro)', key: 'charge', width: 15 },
      { header: 'Abono (Pago)', key: 'credit', width: 15 },
      { header: 'Saldo', key: 'balance', width: 15 }
    ];

    this.movements.forEach(m => {
      worksheet.addRow({
        date: new Date(m.date).toLocaleDateString(),
        description: m.description,
        charge: m.charge,
        credit: m.credit,
        balance: m.balance
      });
    });

    workbook.xlsx.writeBuffer().then(buffer => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `Analisis_Cuenta_${new Date().getTime()}.xlsx`);
    });
  }

  // Abre el modal interactivo de exportación de pagos
  openPaymentExportModal(content: TemplateRef<any>): void {
    const closedId = this.periodService.latestClosedPeriodId();
    if (!closedId) {
      Swal.fire('Atención', 'No hay periodos cerrados para contabilizar en ASINET.', 'info');
      return;
    }
    this.asinetPeriodId.set(closedId);
    this.modalService.open(content, { size: 'lg', backdrop: 'static' });
    this.loadAsinetPayments();
  }

  // Cambia el periodo cerrado del modal y recarga abonos
  onAsinetPeriodChange(periodId: number | null): void {
    this.asinetPeriodId.set(periodId);
    this.loadAsinetPayments();
  }

  // Carga todos los abonos del periodo (pendientes + contabilizados)
  loadAsinetPayments(): void {
    const periodId = this.asinetPeriodId();
    if (!periodId) {
      this.asinetPayments.set([]);
      this.selectedPaymentUuids.set(new Set());
      return;
    }
    this.isPaymentExportLoading.set(true);
    this.paymentService.getAccountAnalysis({
      periodStartId: periodId,
      periodEndId: periodId
    }).subscribe({
      next: (res: any) => {
        const movements = Array.isArray(res) ? res : (res?.movements ?? []);
        const payments = movements.filter((m: any) => m.type === 'PAGO' && m.uuid);
        this.asinetPayments.set(payments);
        this._selectDefaultPending(payments);
        this.isPaymentExportLoading.set(false);
      },
      error: () => {
        this.asinetPayments.set([]);
        this.selectedPaymentUuids.set(new Set());
        this.isPaymentExportLoading.set(false);
        Swal.fire('Error', 'No se pudieron cargar los abonos del periodo', 'error');
      }
    });
  }

  /** Al cargar: marcar solo pendientes de contabilizar. */
  private _selectDefaultPending(payments: any[]): void {
    const uuids = payments.filter((p) => !p.isAccounted && p.uuid).map((p) => p.uuid as string);
    this.selectedPaymentUuids.set(new Set(uuids));
  }

  isPaymentSelected(uuid: string | null | undefined): boolean {
    return Boolean(uuid && this.selectedPaymentUuids().has(uuid));
  }

  togglePaymentSelection(uuid: string | null | undefined, checked: boolean): void {
    if (!uuid) return;
    const next = new Set(this.selectedPaymentUuids());
    if (checked) next.add(uuid);
    else next.delete(uuid);
    this.selectedPaymentUuids.set(next);
  }

  /** Header: marcar/desmarcar todos los pendientes (no toca contabilizados). */
  toggleSelectAllPending(checked: boolean): void {
    const next = new Set(this.selectedPaymentUuids());
    const pending = this.asinetPayments().filter((p) => !p.isAccounted && p.uuid);
    pending.forEach((p) => {
      if (checked) next.add(p.uuid);
      else next.delete(p.uuid);
    });
    this.selectedPaymentUuids.set(next);
  }

  // Exporta copia ASINET de filas marcadas (sin marcar contabilizado)
  exportPaymentsOnly(modalRef?: any): void {
    const rows = this.selectedAsinetPayments();
    if (!rows.length) {
      Swal.fire('Atención', 'Seleccioná al menos un abono para exportar la copia.', 'info');
      return;
    }
    if (modalRef) modalRef.close();
    this._downloadAsinetPaymentWorkbook(rows);
  }

  // Confirma y contabiliza solo pendientes marcados
  exportAndAccountPayments(modalRef?: any): void {
    const rows = this.selectedPendingPayments();
    if (!rows.length) {
      Swal.fire('Atención', 'Seleccioná abonos pendientes de contabilizar.', 'info');
      return;
    }
    Swal.fire(this._paymentConfirmOptions(rows.length)).then((r) => {
      if (r.isConfirmed) this._executeAccountPayments(rows, modalRef);
    });
  }

  // Opciones de confirmación SweetAlert2
  private _paymentConfirmOptions(count: number): any {
    return {
      title: '¿Exportar y Contabilizar?',
      text: `Se exportarán ${count} abono(s) ASINET y se marcarán como contabilizados.`,
      icon: 'question' as const,
      showCancelButton: true,
      confirmButtonText: 'Sí, Contabilizar',
      cancelButtonText: 'Cancelar'
    };
  }

  // Ejecuta descarga y llamada al backend para marcar contabilizados
  private _executeAccountPayments(rows: any[], modalRef?: any): void {
    if (modalRef) modalRef.close();
    this._downloadAsinetPaymentWorkbook(rows);
    const uuids = rows.map((r) => r.uuid).filter(Boolean);
    this.paymentService.markAccounted(uuids).subscribe({
      next: () => this._onPaymentAccountSuccess(),
      error: (err) => Swal.fire('Error', err.error?.error ?? 'Error al contabilizar', 'error')
    });
  }

  // Notifica éxito y recarga el análisis de cuenta
  private _onPaymentAccountSuccess(): void {
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: 'Los abonos quedaron contabilizados en ASINET',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true
    });
    this.loadAnalysis();
    this.loadAsinetPayments();
  }

  // Genera y descarga archivo ASINET para abonos
  private _downloadAsinetPaymentWorkbook(rows: any[]): void {
    const asinetRows = rows.flatMap((p) => this._buildAsinetPaymentRows(p));
    this.asinetExport.download({
      sheetName: 'Transaction_Entry',
      rows: asinetRows,
      fileName: `Pagos_entidades_ASINET_${this.asinetExport.dateStamp()}`
    });
  }

  // Filas de débito banco + crédito Cta Cte entidad por cada pago
  private _buildAsinetPaymentRows(p: any): AsinetRow[] {
    const amount = Number(p.credit ?? 0);
    const transferDate = this.asinetExport.formatDateDot(p.date ?? p.paymentDate);
    const periodLabel = `${String(p.period ?? 0).padStart(2, '0')}/${p.year ?? ''}`;
    const campus = this._campusNameForPayment(p);
    const descBank = `Tr ${transferDate} ${campus}`;
    const desc = `Pago FPV prima ${periodLabel} - ${transferDate}`;
    const ent = this.catalogService.entities().find((e) => Number(e.code) === Number(p.entityCode));
    const entAccount = ent?.group === 'CORPORACION' ? '1136001' : '1136010';
    return [
      {
        AccountCode: '1112001',
        SubAccountCode: '221712270',
        FundCode: 51,
        FunctionCode: '0000',
        RestrictionCode: '0A',
        EntityValue: amount,
        SendMemo: 'False',
        Description: descBank
      },
      {
        AccountCode: entAccount,
        SubAccountCode: String(p.entityCode ?? ''),
        FundCode: 51,
        FunctionCode: '0000',
        RestrictionCode: '0A',
        EntityValue: amount * -1,
        SendMemo: 'False',
        Description: desc
      }
    ];
  }

  /** Nombre corto de campus para glosa banco ASINET (ej. ANCh). */
  private _campusNameForPayment(p: any): string {
    const fromRel = p?.entity?.campus || p?.entity?.name;
    if (fromRel) return String(fromRel).trim();
    const entity = this.catalogService.entities().find((e) => Number(e.code) === Number(p?.entityCode));
    return String(entity?.campus || entity?.name || `Entidad ${p?.entityCode ?? ''}`).trim();
  }

  // --- Cobro consolidado a entidades (ASINET) ---

  openEntityBillingModal(content: TemplateRef<any>): void {
    const closedId = this.periodService.latestClosedPeriodId();
    if (!closedId) {
      Swal.fire('Atención', 'No hay periodos cerrados para contabilizar en ASINET.', 'info');
      return;
    }
    this.asinetPeriodId.set(closedId);
    this.isEntityBillingLoading.set(true);
    this.modalService.open(content, { size: 'lg', backdrop: 'static' });
    this.loadAsinetEntityBilling();
  }

  onAsinetEntityPeriodChange(periodId: number | null): void {
    this.asinetPeriodId.set(periodId);
    this.loadAsinetEntityBilling();
  }

  loadAsinetEntityBilling(): void {
    const period = this.asinetPeriod();
    if (!period?.uuid) {
      this.entityBillingItems.set([]);
      this.entityBillingTotal.set(0);
      this.isEntityBillingLoading.set(false);
      return;
    }
    this.isEntityBillingLoading.set(true);
    this.entityBillingService.getByPeriod(period.uuid).subscribe({
      next: (res) => {
        this.entityBillingItems.set(res.items ?? []);
        this.entityBillingTotal.set(res.totalAmount ?? 0);
        this.isEntityBillingLoading.set(false);
      },
      error: () => {
        this.entityBillingItems.set([]);
        this.entityBillingTotal.set(0);
        this.isEntityBillingLoading.set(false);
        Swal.fire('Error', 'No se pudieron cargar los cobros por entidad', 'error');
      }
    });
  }

  exportEntitiesOnly(modalRef?: any): void {
    if (modalRef) modalRef.close();
    this.exportAsinetEntity();
  }

  exportAndAccountEntities(modalRef?: any): void {
    const period = this.asinetPeriod();
    if (!period || String(period.statusSlug || '').toUpperCase() === 'ACTIVE') {
      Swal.fire('Atención', 'El periodo debe estar cerrado para contabilizar', 'warning');
      return;
    }
    Swal.fire({
      title: '¿Exportar y Contabilizar?',
      text: 'Se generará el archivo ASINET y se marcará el cobro de entidades como contabilizado.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, Contabilizar',
      cancelButtonText: 'Cancelar'
    }).then((r) => {
      if (!r.isConfirmed) return;
      if (modalRef) modalRef.close();
      this.exportAsinetEntity();
      this.entityBillingService.markAccounted(period.uuid).subscribe({
        next: () => {
          this.periodService.reload();
          this.loadAnalysis();
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            title: 'El cobro a entidades ha sido contabilizado.',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true
          });
        },
        error: (e) => Swal.fire('Error', e.error?.error ?? 'Error al contabilizar', 'error')
      });
    });
  }

  private exportAsinetEntity(): void {
    const items = this.entityBillingItems();
    const period = this.asinetPeriod();
    if (!items.length || !period) return;

    const rows: AsinetRow[] = [];
    items.forEach((item: any) => {
      const entity = this.catalogService.entities().find(e => Number(e.code) === Number(item.entityCode));
      const accountCode = (entity?.group === 'CORPORACION') ? '1136001' : '1136010';
      const description = `Cobro Prima FPV ${this.asinetExport.monthName(period.period)} ${period.year ?? ''}`.trim();
      const total = Number(item.amount) || 0;
      rows.push({
        AccountCode: String(accountCode),
        SubAccountCode: String(item.entityCode ?? ''),
        FundCode: 51,
        FunctionCode: '0000',
        RestrictionCode: '0A',
        EntityValue: total,
        SendMemo: 'False',
        Description: description
      });
      rows.push({
        AccountCode: '3188001',
        SubAccountCode: String(item.entityCode ?? ''),
        FundCode: 51,
        FunctionCode: '980312',
        RestrictionCode: '2',
        EntityValue: total * -1,
        SendMemo: 'False',
        Description: description
      });
    });

    this.asinetExport.download({
      sheetName: 'Cobros Entidades',
      rows,
      fileName: `Cobros_entidades_${this.asinetExport.dateStamp()}`
    });
  }
}
